import { useLayoutEffect, type Dispatch, type SetStateAction } from 'react';

/**
 * Resets one or more boolean state values when the component is hidden
 * by Activity (Next.js Cache Components / cacheComponents: true).
 *
 * Activity hides pages with `display: none` instead of unmounting them,
 * so transient UI (dropdowns, popovers, modals) stays open when the user
 * navigates away and returns. This hook runs cleanup synchronously via
 * useLayoutEffect to close them.
 *
 * @example
 * const [isOpen, setIsOpen] = useState(false);
 * useResetOnHide(setIsOpen);
 *
 * @example — multiple setters
 * useResetOnHide(setDropdownOpen, setModalOpen, setTooltipVisible);
 */
export function useResetOnHide(
  ...setters: Dispatch<SetStateAction<boolean>>[]
) {
  useLayoutEffect(() => {
    return () => {
      for (const setter of setters) {
        setter(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
